/* ──────────────────────────────────────────────────────────────────────────────
   NAMA SEVK BELGELERİ — back-office liste  ·  /nama-documents  (menü 9551)

   ══ BU LİSTE FATURA/SİPARİŞ LİSTESİ DEĞİLDİR ══════════════════════════════════
   Satıcı HER ZAMAN Sütaş'tır; belge SAP/telsel'de doğar ve orada faturalaşır.
   Tüccar plasiyer yalnız taşır ve teslim eder. Bu yüzden ekran kendi belge
   hattındadır (/invoices, /orders ile karıştırılmaz) ve "Satıcı" kolonu her
   satırda kimin adına teslim edildiğini söyler.

   ══ LİSTE = c360 (useC360List) ════════════════════════════════════════════════
   Sayfalama/sıralama/kolon filtresi SUNUCUDA (OData). Durum ve Kaynak STRING
   alanlardır → huni değerleri tırnaklanır (namaShared: valueFormat 'string').
   Müşteri etiketleri lazy çekilir (4000+ müşteri tümüyle indirilmez — InvoicesPage
   ile aynı desen).
   ────────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, getODataRows, toGridSortField, toODataSortField,
  newRecordButtonStyle,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser, ViewIcon, EditIcon, unwrapLookupItems, buildSelectFields,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { fmtDate, fmtMoney } from './returnShared';
import {
  NAMA_API, statusLabel, statusTone, sourceLabel, sourceStyle, docTypeLabel,
  STATUS_SELECT_OPTIONS, SOURCE_SELECT_OPTIONS, DOC_TYPE_SELECT_OPTIONS,
  SELLER_STATEMENT, sellerNameOf, isOpen,
} from './namaShared';

export function NamaDocumentsPage() {
  const navigate = useNavigate();
  const { canAdd, canEdit } = usePageActions();
  usePageMeta('Nama Sevk Belgeleri', 'Tüccar plasiyerin Sütaş adına teslim ettiği belgeler. Satıcı Sütaş\'tır; belge SAP/telsel\'den gelir.');

  const [toast, setToast] = useState(null);
  const [selected, setSelected] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [representatives, setRepresentatives] = useState([]);
  const [customerLabels, setCustomerLabels] = useState({}); // id -> etiket (lazy)

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // Huniler: Durum · Kaynak · Tip. Hepsi STRING alan → tırnaklı OData cümlesi.
  const columnFilterMeta = useMemo(() => ({
    status: { type: 'select', odataField: 'Status', valueFormat: 'string', options: STATUS_SELECT_OPTIONS },
    source: { type: 'select', odataField: 'Source', valueFormat: 'string', options: SOURCE_SELECT_OPTIONS },
    docType: { type: 'select', odataField: 'DocType', valueFormat: 'string', options: DOC_TYPE_SELECT_OPTIONS },
    externalNo: { type: 'text', odataField: 'ExternalNo' },
    netAmount: { type: 'number', odataField: 'NetAmount' },
  }), []);

  // Serbest arama yalnız string alanlarda (int/guid'e contains yapılmaz).
  const searchFields = useMemo(() => ['ExternalNo', 'PrintedWaybillNo', 'SellerName'], []);

  // $select: hücrelerin okuduğu HER alan burada olmalı. customerLabel/representativeLabel
  // display kolonları CustomerId/RepresentativeId okur (meta'da yok) → always'te.
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta,
    columnVisibility,
    always: [
      'Id', 'ExternalNo', 'DocType', 'Source', 'CustomerId', 'RepresentativeId',
      'SellerName', 'Status', 'NetAmount', 'ReceivedAt', 'DeliveredAt',
    ],
  }), [columnFilterMeta, columnVisibility]);

  const list = useC360List({
    apiBase: NAMA_API,
    columnFilterMeta,
    searchFields,
    defaultSortField: 'ReceivedAt',
    defaultSortDir: 'desc',
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  // Temsilci lookup'ı küçüktür → tek atımda.
  useEffect(() => {
    fetch('/api/representatives/Select', { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setRepresentatives(unwrapLookupItems(data)))
      .catch(() => { /* lookup yoksa kolon "-" gösterir; liste yine çalışır */ });
  }, []);

  const representativeName = useMemo(() => {
    const map = new Map(representatives.map((r) => [r.id, r.name || r.textId || '(adsız)']));
    return (id) => (id ? map.get(id) || '-' : '-');
  }, [representatives]);

  // Müşteri etiketleri sayfadaki id'ler kadar çekilir (tüm müşteri listesi İNDİRİLMEZ).
  const customerName = useCallback((id) => (id ? (customerLabels[id] || '…') : '-'), [customerLabels]);
  const custFetchRef = useRef(new Set());
  const ensureCustomerLabels = useCallback(async (ids) => {
    const need = [...new Set(ids.filter((id) => id && customerLabels[id] === undefined && !custFetchRef.current.has(id)))];
    if (!need.length) return;
    need.forEach((id) => custFetchRef.current.add(id));
    try {
      for (let i = 0; i < need.length; i += 50) {
        const chunk = need.slice(i, i + 50);
        const filter = chunk.map((id) => `Id eq ${id}`).join(' or ');
        const url = `/api/customers?$top=500&$filter=${encodeURIComponent(`(${filter})`)}`;
        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(url, { headers: authHeaders() });
        const found = {};
        if (res.ok) {
          // eslint-disable-next-line no-await-in-loop
          const data = await res.json();
          getODataRows(data).forEach((c) => { found[c.id] = c.name || c.textId || '-'; });
        }
        setCustomerLabels((prev) => {
          const next = { ...prev };
          chunk.forEach((id) => { next[id] = found[id] || '-'; });
          return next;
        });
      }
    } finally {
      need.forEach((id) => custFetchRef.current.delete(id));
    }
  }, [customerLabels]);

  useEffect(() => {
    if (rows.length) ensureCustomerLabels(rows.map((r) => r.customerId).filter(Boolean));
  }, [rows, ensureCustomerLabels]);

  const openView = (row = selected) => { if (row?.id) navigate(`/nama-documents/${row.id}`); };
  // #282: kalem butonu düzenleme niyetini AÇIKÇA taşır (göz/çift-tık izleme modunda açar).
  const openEdit = (row = selected) => { if (row?.id) navigate(`/nama-documents/${row.id}?edit=1`); };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('externalNo', {
      header: 'Belge No', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('docType', {
      header: 'Tip', meta: { filterType: 'select' },
      cell: (info) => docTypeLabel(info.getValue()),
    }),
    columnHelper.accessor('source', {
      header: 'Kaynak', meta: { filterType: 'select' },
      cell: (info) => <span style={sourceStyle(info.getValue())}>{sourceLabel(info.getValue())}</span>,
    }),
    columnHelper.display({
      id: 'customerLabel', header: 'Müşteri', enableSorting: false,
      cell: ({ row }) => customerName(row.original.customerId),
    }),
    columnHelper.display({
      id: 'representativeLabel', header: 'Temsilci', enableSorting: false,
      cell: ({ row }) => representativeName(row.original.representativeId),
    }),
    // Satıcı BİZ DEĞİLİZ — her satırda görünür (belge Sütaş adına teslim edilir).
    columnHelper.accessor('sellerName', {
      header: 'Satıcı', enableSorting: false,
      cell: ({ row }) => <span style={{ fontWeight: 700 }}>{sellerNameOf(row.original)}</span>,
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => <StatusBadge tone={statusTone(info.getValue())}>{statusLabel(info.getValue())}</StatusBadge>,
    }),
    columnHelper.accessor('netAmount', {
      header: 'Net Tutar', meta: { filterType: 'number' },
      cell: (info) => <div style={{ width: '100%', textAlign: 'right' }}>{fmtMoney(info.getValue())}</div>,
    }),
    columnHelper.accessor('receivedAt', {
      header: 'Geliş Tarihi',
      cell: (info) => fmtDate(info.getValue()),
    }),
    columnHelper.accessor('deliveredAt', {
      header: 'Teslim Tarihi',
      cell: (info) => (info.getValue() ? fmtDate(info.getValue()) : '—'),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false, enableSorting: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view"
                  title="Görüntüle" aria-label="Görüntüle"
                  onClick={(e) => { e.stopPropagation(); openView(row.original); }}>
            <ViewIcon />
          </button>
          {/* Teslim/iptal yalnız AÇIK belgede anlamlıdır — kapanmış belgede düzenleme butonu çizilmez. */}
          {canEdit && isOpen(row.original.status) && (
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit"
                    title="Teslimatı Kaydet" aria-label="Teslimatı Kaydet"
                    onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}>
              <EditIcon />
            </button>
          )}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [customerName, representativeName, canEdit]);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('ReceivedAt', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    try {
      const cols = [
        { headerName: 'Belge No', valueGetter: ({ row }) => row.externalNo || '' },
        { headerName: 'Tip', valueGetter: ({ row }) => docTypeLabel(row.docType) },
        { headerName: 'Kaynak', valueGetter: ({ row }) => sourceLabel(row.source) },
        { headerName: 'Müşteri', valueGetter: ({ row }) => customerName(row.customerId) },
        { headerName: 'Temsilci', valueGetter: ({ row }) => representativeName(row.representativeId) },
        { headerName: 'Satıcı', valueGetter: ({ row }) => sellerNameOf(row) },
        { headerName: 'Durum', valueGetter: ({ row }) => statusLabel(row.status) },
        { headerName: 'Net Tutar', valueGetter: ({ row }) => Number(row.netAmount || 0) },
        { headerName: 'Geliş Tarihi', valueGetter: ({ row }) => fmtDate(row.receivedAt) },
        { headerName: 'Teslim Tarihi', valueGetter: ({ row }) => (row.deliveredAt ? fmtDate(row.deliveredAt) : '') },
      ];
      const count = await exportRows(cols, `nama-sevk-belgeleri_${new Date().toISOString().slice(0, 10)}`);
      if (!count) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarılamadı');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Belge no, irsaliye no veya satıcı ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && (
        <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>
      )}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15,23,42,0.10))' }}>
        <button type="button" title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}
                style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <ExcelIcon size={16} />
        </button>
      </span>
    </>
  );

  const metaNode = (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <StatusBadge tone="neutral">{totalCount} belge</StatusBadge>
    </span>
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Nama Sevk Belgeleri"
        subtitle={SELLER_STATEMENT}
        meta={metaNode}
        // Belge normalde SAP/telsel ingest'inden ya da mobilden doğar; "Yeni" masabaşı
        // girişi (entegrasyon gecikmesi/telsel siparişi) içindir — satıcıyı yine sunucu belirler.
        actions={canAdd && (
          <button style={newRecordButtonStyle} onClick={() => navigate('/nama-documents/new')}>
            + Yeni Belge
          </button>
        )}
        toolbar={toolbarNode}
        footer={(
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={pageSize}
            rowCount={rows.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      >
        <C360Table
          table={table}
          columnFilterMeta={columnFilterMeta}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openView}
          emptyText="Nama sevk belgesi bulunamadı"
        />
      </ListPageShell>

      <Toast toast={toast} />
    </div>
  );
}

export default NamaDocumentsPage;
